// API Management Route
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Key, FileText } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { useAuth } from '@/providers/AuthProvider';
import api from '@/lib/api-client';
import { APIKeysTab } from './api-keys.js';
import { SpecificationTab } from './specification.js';
import { RoutePermissionsTab } from './route-permissions.js';

export const Route = createFileRoute('/key-management/')({
  component: APIManagement,
});

function APIManagement() {
  const { user: authUser } = useAuth();
  const { data: profile } = useQuery({
    queryKey: ['user-profile', authUser?.localAccountId],
    queryFn: async () => {
      const response = await api.users.me();
      return (response && typeof response === 'object' && 'data' in response)
        ? (response as any).data
        : response;
    },
    enabled: !!authUser,
    staleTime: 5 * 60 * 1000,
  });
  const isAdmin = profile?.role === 'admin';

  const [activeTab, setActiveTab] = useState<'api-keys' | 'integrations' | 'specification' | 'route-permissions'>('api-keys');

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold">API Management</h1>
        <p className="text-muted-foreground mt-1">
          Manage your API keys, Route Permissions, and API specifications
        </p>
      </div>

      <div className="flex gap-8">
        {/* Sidebar */}
        <div className="w-64 space-y-1">
          <APIManagementNavItem
            icon={<Key className="w-5 h-5" />}
            label="API Keys"
            active={activeTab === 'api-keys'}
            onClick={() => setActiveTab('api-keys')}
          />
          <APIManagementNavItem
            icon={<FileText className="w-5 h-5" />}
            label="Specification"
            active={activeTab === 'specification'}
            onClick={() => setActiveTab('specification')}
          />
          {isAdmin && (
            <APIManagementNavItem
              icon={<FileText className="w-5 h-5" />}
              label="API Route Permissions"
              active={activeTab === 'route-permissions'}
              onClick={() => setActiveTab('route-permissions')}
            />
          )}
        </div>

        {/* Content */}
        <div className="flex-1">
          {activeTab === 'api-keys' && <APIKeysTab />}
          {activeTab === 'specification' && <SpecificationTab />}
          {activeTab === 'route-permissions' && isAdmin && <RoutePermissionsTab />}
        </div>
      </div>
    </div>
  );
}

function APIManagementNavItem({
  icon,
  label,
  active,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex items-center gap-3 w-full px-4 py-3 rounded-lg transition-colors',
        active
          ? 'bg-accent-500/10 text-accent-500'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
      )}
    >
      {icon}
      {label}
    </button>
  );
}
